import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowRight,
  BarChart3,
  BadgeCheck,
  BrainCircuit,
  Building2,
  CreditCard,
  Landmark,
  Layers3,
  ListChecks,
  LockKeyhole,
  Mail,
  MessageSquareOff,
  Network,
  ShieldCheck,
  Smartphone,
  UsersRound,
  WalletCards,
  Workflow,
} from "lucide-react";
import { defaultBillingTiers } from "./billingPlans";
import { NexusFooter, NexusPublicNav } from "./PublicSiteComponents";
import { landingMobileScreenshot } from "./mobileScreenshots";
import "./LandingPage.css";

const storeItems = [
  {
    id: "tech_hoodie",
    name: "Nexus Tech Hoodie",
    price: "64.99",
    tone: "hoodie",
    description: "Heavyweight black fleece with cyber yellow trim.",
  },
  {
    id: "community_tee",
    name: "Nexus Community Tee",
    price: "29.99",
    tone: "tee",
    description: "Structured cotton tee for organization teams.",
  },
  {
    id: "field_jacket",
    name: "Nexus Field Jacket",
    price: "89.99",
    tone: "jacket",
    description: "Minimal shell for staff and launch events.",
  },
  {
    id: "operator_cap",
    name: "Nexus Operator Cap",
    price: "24.99",
    tone: "cap",
    description: "Low-profile cap with tonal Nexus mark.",
  },
];

const capabilityRows = [
  ["Ledgers", "Member limits and records", "Controlled"],
  ["Accounts", "Internal wallet management", "Traceable"],
  ["Analytics", "Margins and activity trends", "AI-ready"],
  ["Mobile", "Member requests and RSVPs", "Connected"],
];

const featureCards = [
  {
    icon: Layers3,
    title: "Administrative Ledgers",
    copy: "Track member records, limits, balances, and history from a single source of truth.",
  },
  {
    icon: Workflow,
    title: "Management Log",
    copy: "Capture activity, expenses, events, and key decisions without scattered sheets.",
  },
  {
    icon: UsersRound,
    title: "Member Directory",
    copy: "Store member profiles, contact details, activity context, and performances in one clean view.",
  },
  {
    icon: WalletCards,
    title: "Operating Accounts",
    copy: "Organize internal wallets, transfers, expenses, and balance movements across your club.",
  },
  {
    icon: ShieldCheck,
    title: "Staff Permissions",
    copy: "Give managers and team members the tools they need with custom role-based access.",
  },
  {
    icon: ListChecks,
    title: "Operational Flow Tools",
    copy: "Run waitlists, RSVP lists, event schedules, and member movement from one live control surface.",
  },
];

const intelligenceCards = [
  "Margin trends by session and month",
  "Expense tracking by category and operating account",
  "Activity trends across members, tables, and events",
  "AI-powered recommendations for operational efficiency",
];

const mobileFeatures = [
  "Members can join your club through Nexus Mobile",
  "View current tables, schedules, and upcoming events",
  "Request waitlist spots and send RSVPs from the app",
  "Track loyalty points, tiers, rewards, and referrals",
];

const workflowSteps = [
  [
    "Subscribe",
    "Select your plan and start a 14 day trial through secure Stripe Checkout.",
  ],
  [
    "Activate",
    "Receive the onboarding email and launch your own Nexus workspace.",
  ],
  [
    "Configure",
    "Set club preferences, settings, permissions, and operating accounts.",
  ],
  ["Onboard Team", "Invite staff members via email with a single click."],
  [
    "Launch",
    "Add members and manage daily operations from one command center.",
  ],
];

const faqs = [
  [
    "Can I try Nexus before paying?",
    "Yes. Every plan can start with a 14 day free trial through secure Stripe Checkout.",
  ],
  [
    "Is Nexus Mobile included in all plans?",
    "Nexus Mobile is included with Standard and Pro. Basic is built for core operational records only.",
  ],
  [
    "Can staff have different access levels?",
    "Yes. Nexus supports custom permissions so owners can control what each team member can view or manage.",
  ],
  [
    "What happens after checkout?",
    "Nexus sends an onboarding email so you can activate your workspace, configure settings, and invite your team.",
  ],
];

export default function LandingPage() {
  const tiers = defaultBillingTiers;
  const [selectedTier, setSelectedTier] = useState("pro");
  const [trialEnabled, setTrialEnabled] = useState(true);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [adminEmail, setAdminEmail] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [checkoutStatus, setCheckoutStatus] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const pricingRef = useRef(null);
  const storeRef = useRef(null);
  const signupRef = useRef(null);
  const emailRef = useRef(null);
  const location = useLocation();

  const moveToPricing = () => {
    pricingRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const moveToStore = () => {
    storeRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const selectedTierLabel = useMemo(
    () => tiers.find((tier) => tier.id === selectedTier)?.name || "Pro",
    [selectedTier, tiers],
  );
  const selectedTierPrice = useMemo(() => {
    const tier = tiers.find((item) => item.id === selectedTier) || tiers[2];
    return billingCycle === "annual" ? tier.annualPrice : tier.monthlyPrice;
  }, [billingCycle, selectedTier, tiers]);

  const moveToSignup = (tierId) => {
    setSelectedTier(tierId);
    signupRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    window.setTimeout(() => emailRef.current?.focus(), 450);
  };

  useEffect(() => {
    const section =
      new URLSearchParams(location.search).get("section") ||
      location.state?.section;

    if (section === "pricing") {
      window.setTimeout(moveToPricing, 80);
    }

    if (section === "store") {
      window.setTimeout(moveToStore, 80);
    }
  }, [location.search, location.state]);

  const createCheckout = async ({ itemId } = {}) => {
    const email = adminEmail.trim();
    const orgName = organizationName.trim();
    if (!email || !orgName) {
      setCheckoutStatus(
        "Enter your organization name and Primary Administrative Email first.",
      );
      emailRef.current?.focus();
      return;
    }

    setIsSubmitting(true);
    setCheckoutStatus(
      itemId ? "Preparing store checkout..." : "Preparing Stripe Checkout...",
    );

    try {
      const response = await fetch(
        itemId
          ? "/api/stripe/create-store-checkout-session"
          : "/api/stripe/create-checkout-session",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(
            itemId
              ? { email, organizationName: orgName, itemId }
              : {
                  email,
                  organizationName: orgName,
                  tier: selectedTier,
                  trial: trialEnabled,
                  billingCycle,
                },
          ),
        },
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Stripe Checkout could not be started.");
      }

      window.location.assign(data.url);
    } catch (err) {
      setCheckoutStatus(err.message);
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    createCheckout();
  };

  return (
    <main className="nexus-home">
      <NexusPublicNav onPricingClick={moveToPricing} onStoreClick={moveToStore} />

      <section className="nexus-hero" aria-labelledby="hero-title">
        <div className="nexus-hero__scene" aria-hidden="true">
          <div className="nexus-console">
            <div className="nexus-console__bar">
              <span />
              <span />
              <span />
            </div>
            <div className="nexus-console__grid">
              {capabilityRows.map(([label, detail, state]) => (
                <div className="nexus-console__row" key={label}>
                  <span>{label}</span>
                  <strong>{detail}</strong>
                  <em>{state}</em>
                </div>
              ))}
            </div>
            <div className="nexus-console__rail">
              <span />
              <span />
              <span />
              <span />
              <span />
            </div>
          </div>
          <div className="nexus-circuit">
            {Array.from({ length: 36 }).map((_, index) => (
              <span key={index} />
            ))}
          </div>
        </div>

        <div className="nexus-hero__content">
          <p className="nexus-eyebrow">
            Financial and operations control center
          </p>
          <h1 id="hero-title">
            The Operating System for Private Clubs
          </h1>
          <p className="nexus-hero__subhead">
            Streamline your private club's logistics, memberships, financials,
            and administrative ledgering in one unified interface.
          </p>
          <div className="nexus-hero__actions">
            <button
              className="nexus-button nexus-button--primary"
              onClick={moveToPricing}
            >
              Launch Your Club
            </button>
            <Link className="nexus-button nexus-button--ghost" to="/login">
              Login
            </Link>
          </div>
          <p className="nexus-trust">
            Trusted by private membership organizations nationwide.
          </p>
          <div className="nexus-hero__metrics" aria-label="Platform highlights">
            <span>
              <strong>Operational Records</strong>
              replace your excel spreadsheets and paper lists
            </span>
            <span>
              <strong>Management Log</strong>
              keep track of all your staff and member activity
            </span>
            <span>
              <strong>Member Directory</strong>
              profiles, limits, notes, and access all in one place
            </span>
          </div>
        </div>
      </section>

      <section
        className="nexus-section nexus-section--systems"
        id="features"
        aria-label="Operating system overview"
      >
        <div className="nexus-section__heading">
          <p className="nexus-eyebrow">Operations Hub</p>
          <h2>One command center for the records that run your club</h2>
        </div>
        <div className="nexus-systems">
          {featureCards.map(({ icon: Icon, title, copy }) => (
            <article key={title}>
              <Icon size={22} aria-hidden="true" />
              <h3>{title}</h3>
              <p>{copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section
        className="nexus-section nexus-intelligence"
        aria-labelledby="intelligence-title"
      >
        <div className="nexus-section__heading">
          <p className="nexus-eyebrow">Business intelligence</p>
          <h2 id="intelligence-title">
            Make smarter decisions with real club data
          </h2>
        </div>
        <div className="nexus-intelligence__layout">
          <div className="nexus-intelligence__copy">
            <BrainCircuit size={26} aria-hidden="true" />
            <h3>AI-powered analytics for margins and trends</h3>
            <p>
              Nexus helps owners understand what is happening across expenses,
              operating accounts, member activity, staff activity, and club flow
              without rebuilding reports from messy spreadsheets.
            </p>
          </div>
          <div className="nexus-intelligence__list">
            {intelligenceCards.map((item) => (
              <span key={item}>
                <BarChart3 size={18} aria-hidden="true" />
                {item}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section
        className="nexus-section nexus-mobile"
        id="mobile"
        aria-labelledby="mobile-title"
      >
        <div className="nexus-section__heading">
          <p className="nexus-eyebrow">Member experience</p>
          <h2 id="mobile-title">
            Let members connect with your club from their phone
          </h2>
        </div>
        <div className="nexus-mobile__layout">
          <div className="nexus-phone" aria-label="Nexus Mobile app preview">
            <img
              className="nexus-phone__image"
              src={landingMobileScreenshot.src}
              alt={landingMobileScreenshot.alt}
            />
          </div>
          <div className="nexus-mobile__copy">
            <MessageSquareOff size={26} aria-hidden="true" />
            <h3>Reduce constant text updates.</h3>
            <p>
              Members can check the current state of your club, join through the
              app, request waitlist spots, send RSVPs, view schedules, and track
              loyalty progress without asking your team for every update.
            </p>
            <div className="nexus-mobile__features">
              {mobileFeatures.map((feature) => (
                <span key={feature}>
                  <Smartphone size={17} aria-hidden="true" />
                  {feature}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section
        className="nexus-section nexus-workflow"
        aria-labelledby="workflow-title"
      >
        <div className="nexus-section__heading">
          <p className="nexus-eyebrow">How it works</p>
          <h2 id="workflow-title">
            Subscribe, configure, and launch with structure
          </h2>
        </div>
        <div className="nexus-workflow__steps">
          {workflowSteps.map(([title, copy], index) => (
            <article key={title}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <h3>{title}</h3>
              <p>{copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section
        className="nexus-section nexus-faq"
        id="faq"
        aria-labelledby="faq-title"
      >
        <div className="nexus-section__heading">
          <p className="nexus-eyebrow">FAQ</p>
          <h2 id="faq-title">Everything you need to know</h2>
        </div>
        <div className="nexus-faq__grid">
          {faqs.map(([question, answer]) => (
            <article key={question}>
              <h3>{question}</h3>
              <p>{answer}</p>
            </article>
          ))}
        </div>
      </section>

      <section
        className="nexus-section"
        id="pricing"
        ref={pricingRef}
        aria-labelledby="pricing-title"
      >
        <div className="nexus-section__heading nexus-section__heading--split">
          <div>
            <p className="nexus-eyebrow">Pricing</p>
            <h2 id="pricing-title">Start with a 14-day free trial</h2>
          </div>
          <div className="nexus-pricing-controls">
            <button
              className="nexus-trial"
              role="switch"
              aria-checked={trialEnabled}
              type="button"
              onClick={() => setTrialEnabled((enabled) => !enabled)}
            >
              <span className="nexus-trial__track">
                <span className="nexus-trial__knob" />
              </span>
              <span>14-day free trial</span>
            </button>
            <div className="nexus-billing-toggle" aria-label="Billing cycle">
              <button
                type="button"
                className={billingCycle === "monthly" ? "is-selected" : ""}
                onClick={() => setBillingCycle("monthly")}
              >
                Monthly
              </button>
              <button
                type="button"
                className={billingCycle === "annual" ? "is-selected" : ""}
                onClick={() => setBillingCycle("annual")}
              >
                Annual
              </button>
            </div>
          </div>
        </div>

        <div className="nexus-pricing">
          {tiers.map((tier) => (
            <article
              className={`nexus-price ${tier.featured ? "nexus-price--featured" : ""}`}
              key={tier.id}
            >
              <div className="nexus-price__top">
                <h3>{tier.name}</h3>
                {tier.featured && (
                  <span>
                    <BadgeCheck size={16} aria-hidden="true" /> Recommended
                  </span>
                )}
              </div>
              <p>{tier.summary}</p>
              <div className="nexus-price__amount">
                <strong>
                  $
                  {billingCycle === "annual"
                    ? tier.annualPrice
                    : tier.monthlyPrice}
                </strong>
                <span>/ month{tier.priceQualifier ? ` ${tier.priceQualifier}` : ""}</span>
              </div>
              {tier.usageNote && (
                <p className="nexus-price__billing-note">{tier.usageNote}</p>
              )}
              {billingCycle === "annual" && (
                <p className="nexus-price__billing-note">Billed annually</p>
              )}
              <ul>
                {tier.features.map((feature) => (
                  <li key={feature}>
                    <Network size={15} aria-hidden="true" />
                    {feature}
                  </li>
                ))}
              </ul>
              <button
                className={`nexus-button ${tier.featured ? "nexus-button--primary" : "nexus-button--secondary"}`}
                onClick={() => moveToSignup(tier.id)}
              >
                {trialEnabled ? "Start Trial" : "Get Started"}
              </button>
            </article>
          ))}
        </div>
        <p className="nexus-sales-note">
          Need custom onboarding, multi-club configuration, or dedicated
          support?
          <Link to="/sales"> Contact Sales</Link>.
        </p>
      </section>

      <section
        className="nexus-section nexus-signup"
        ref={signupRef}
        aria-labelledby="signup-title"
      >
        <div className="nexus-signup__copy">
          <p className="nexus-eyebrow">Secure checkout</p>
          <h2 id="signup-title">
            {trialEnabled
              ? "Start your 14-day free trial"
              : "Activate your subscription"}
          </h2>
          <p>
            Enter your organization name and Primary Administrative Email.
            Standard, Pro, and annual plans use verified US bank account
            payments. Pro includes a base subscription plus usage billing for
            active members who played that month. Card payment is available for
            Basic monthly only.
          </p>
          <div className="nexus-payment-row" aria-label="Payment support">
            <span>
              <CreditCard size={18} aria-hidden="true" /> Card
            </span>
            <span>
              <Landmark size={18} aria-hidden="true" /> ACH
            </span>
            <span>
              <LockKeyhole size={18} aria-hidden="true" /> Financial Connections
            </span>
          </div>
        </div>

        <form className="nexus-signup__form" onSubmit={handleSubmit}>
          <label htmlFor="organization-name">Organization Name</label>
          <div className="nexus-email-field">
            <Building2 size={18} aria-hidden="true" />
            <input
              id="organization-name"
              type="text"
              value={organizationName}
              onChange={(event) => setOrganizationName(event.target.value)}
              placeholder="Nexus Social Club"
              required
            />
          </div>
          <label htmlFor="admin-email">Primary Administrative Email</label>
          <div className="nexus-email-field">
            <Mail size={18} aria-hidden="true" />
            <input
              id="admin-email"
              ref={emailRef}
              type="email"
              value={adminEmail}
              onChange={(event) => setAdminEmail(event.target.value)}
              placeholder="admin@organization.com"
              required
            />
          </div>
          <div className="nexus-plan-select" aria-label="Selected pricing tier">
            {tiers.map((tier) => (
              <button
                key={tier.id}
                type="button"
                className={selectedTier === tier.id ? "is-selected" : ""}
                onClick={() => setSelectedTier(tier.id)}
              >
                {tier.name}
              </button>
            ))}
          </div>
          <div
            className="nexus-plan-select"
            aria-label="Selected billing cycle"
          >
            <button
              type="button"
              className={billingCycle === "monthly" ? "is-selected" : ""}
              onClick={() => setBillingCycle("monthly")}
            >
              Monthly
            </button>
            <button
              type="button"
              className={billingCycle === "annual" ? "is-selected" : ""}
              onClick={() => setBillingCycle("annual")}
            >
              Annual
            </button>
          </div>
          <button
            className="nexus-button nexus-button--primary nexus-signup__submit"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? "Opening Stripe..."
              : `${trialEnabled ? "Start Trial" : "Get Started"} - ${selectedTierLabel} $${selectedTierPrice}/mo${selectedTier === "pro" ? " base" : ""}`}
          </button>
          <p className="nexus-signup__status" role="status">
            {checkoutStatus ||
              "Verified bank account payment is required except on Basic monthly."}
          </p>
        </form>
      </section>

      <section
        className="nexus-section"
        id="store"
        ref={storeRef}
        aria-labelledby="store-title"
      >
        <div className="nexus-section__heading">
          <p className="nexus-eyebrow">Nexus Store</p>
          <h2 id="store-title">Merchandise for modern member organizations</h2>
        </div>
        <div className="nexus-store">
          {storeItems.map((item) => (
            <button
              className="nexus-store__item"
              key={item.id}
              type="button"
              onClick={() => createCheckout({ itemId: item.id })}
            >
              <div
                className={`nexus-store__visual nexus-store__visual--${item.tone}`}
                aria-hidden="true"
              >
                <span />
              </div>
              <div className="nexus-store__content">
                <div className="nexus-store__head">
                  <h3>{item.name}</h3>
                  <strong>${item.price}</strong>
                </div>
                <p>{item.description}</p>
              </div>
              <div className="nexus-store__cta" aria-hidden="true">
                <span>Buy now</span>
                <ArrowRight size={17} />
              </div>
            </button>
          ))}
        </div>
      </section>

      <NexusFooter />
    </main>
  );
}
